import { getWebPath } from '@doss/common-all'
import { ReactNode, useMemo } from 'react'
import { Trans } from 'react-i18next'
import { useNavigate } from 'react-router'
import {
  BreadcrumbObjectType,
  useDeleteTableRowWithResultMutation,
  useGetDeleteBlockingCheckQuery,
} from '~/common/generated/apollo-operations'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { DossSignalType, useDossSignals } from '../../common/hooks/useDossSignals'
import { ConfirmationDialog } from '../ConfirmationDialog'
import {
  BlockingReferencesDisplay,
  type BlockingReferenceDisplay,
} from '../shared/BlockingReferencesDisplay'
import { useDossSnackbar } from '../shared/DossSnackbar'
import { DossTableRow } from '../tables/types'
import { RecordDetailTableRow } from './types'

interface DeleteRecordDialogProps {
  tableRow: DossTableRow | RecordDetailTableRow
  openDialog: boolean
  tableId: string
  handleCloseDeleteRecord: () => void
  isChildTableRecord: boolean
  handleDeleteRow?: (rowId: string) => void
}

type DeleteTableRowResultData = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useDeleteTableRowWithResultMutation>[0]>>['data']
>['deleteTableRowWithResult']

const BlockingRowLink = (props: { href: string; rowName: string }) => {
  return (
    <a
      className="text-white underline decoration-white"
      href={props.href}
      rel="noopener noreferrer"
      target="_blank"
    >
      {props.rowName}
    </a>
  )
}

/**
 * Extract blocking reference details from the delete result.
 * Prefers blockingReferences (new) over blockingRows (legacy) for richer context.
 */
const getBlockingReferenceDetails = (deleteResult: DeleteTableRowResultData | null | undefined) => {
  const blockingReferences = deleteResult?.blockingReferences ?? []
  const firstRef = blockingReferences.at(0)

  // If we have blocking references with context, use them
  if (firstRef?.row.id) {
    return {
      blockingRowId: firstRef.row.id,
      blockingRowLabel: firstRef.row.name,
      columnName: firstRef.columnName,
      tableName: firstRef.tableName,
      otherBlockingCount: Math.max(blockingReferences.length - 1, 0),
      hasContext: true,
    }
  }

  // Fallback to legacy blockingRows if no references available
  const blockingRows = deleteResult?.blockingRows ?? []
  const blockingRow = blockingRows.at(0)

  if (!blockingRow?.id) {
    return null
  }

  return {
    blockingRowId: blockingRow.id,
    blockingRowLabel: blockingRow.name,
    columnName: null,
    tableName: null,
    otherBlockingCount: Math.max(blockingRows.length - 1, 0),
    hasContext: false,
  }
}

const getDeleteBlockedMessage = (
  deleteResult: DeleteTableRowResultData | null | undefined,
): string | null => {
  return deleteResult?.errors?.[0]?.message ?? null
}

/**
 * Build the delete blocked message with context (column/table names) when available.
 */
const getDeleteBlockedLinkMessage = (args: {
  blockingRowPath: string
  rowName: string
  columnName: string | null
  tableName: string | null
  otherBlockingCount: number
}): JSX.Element => {
  // If we have column/table context, use the new translation keys
  if (args.columnName && args.tableName) {
    return (
      <Trans
        components={{
          BlockingRowLink: <BlockingRowLink href={args.blockingRowPath} rowName={args.rowName} />,
          ColumnName: <strong>{args.columnName}</strong>,
          TableName: <strong>{args.tableName}</strong>,
        }}
        count={args.otherBlockingCount}
        i18nKey={
          args.otherBlockingCount > 0
            ? 'record.delete_blocked_link_with_context_other'
            : 'record.delete_blocked_link_with_context_one'
        }
        values={{
          count: args.otherBlockingCount,
        }}
      />
    )
  }

  // Fallback to legacy translation keys without context
  return (
    <Trans
      components={{
        BlockingRowLink: <BlockingRowLink href={args.blockingRowPath} rowName={args.rowName} />,
      }}
      count={args.otherBlockingCount}
      i18nKey={
        args.otherBlockingCount > 0
          ? 'record.delete_blocked_link_other'
          : 'record.delete_blocked_link_one'
      }
      values={{
        count: args.otherBlockingCount,
      }}
    />
  )
}

/**
 * Renders confirmation dialog for deleting a record
 */
export function DeleteRecordDialog({
  tableRow,
  openDialog,
  handleCloseDeleteRecord,
  isChildTableRecord,
  tableId,
  handleDeleteRow,
}: DeleteRecordDialogProps) {
  const snackbar = useDossSnackbar()
  const navigate = useNavigate()

  const { t } = useTypedTranslation()
  const { dispatch } = useDossSignals()

  // Pre-flight blocking check - runs when dialog opens
  const { data: blockingCheckData, loading: isCheckingBlocking } = useGetDeleteBlockingCheckQuery({
    variables: {
      input: {
        rowId: tableRow.id,
      },
    },
    skip: !openDialog,
    fetchPolicy: 'network-only',
  })

  // Derive preflight result from query data
  const preflightResult = useMemo(() => {
    if (!openDialog || isCheckingBlocking) {
      return null
    }
    const checkResult = blockingCheckData?.deleteBlockingCheck
    if (checkResult && checkResult.blockedCount > 0) {
      const blockedRow = checkResult.blockedRows[0]
      return {
        isBlocked: true,
        allReferences: blockedRow.blockingReferences.map((ref) => ({
          rowId: ref.row.id,
          rowName: ref.row.name,
          columnName: ref.columnName,
          tableName: ref.tableName,
          tableId: ref.tableId,
        })),
      }
    }
    return { isBlocked: false, allReferences: [] }
  }, [openDialog, isCheckingBlocking, blockingCheckData])

  const [deleteTableRow, { loading: isDeleting }] = useDeleteTableRowWithResultMutation({
    update(cache, { data }) {
      if (!data?.deleteTableRowWithResult.row) {
        return
      }

      const normalizedId = cache.identify(tableRow)
      cache.evict({ id: normalizedId })

      // Update the cache to reflect the new total count
      cache.modify({
        fields: {
          paginatedTableRows(prev) {
            if (prev.rows.some((ref: { __ref: string }) => ref.__ref === normalizedId)) {
              return { ...prev, totalCount: prev.totalCount - 1 }
            } else {
              return prev
            }
          },
        },
      })

      cache.gc()
      dispatch(DossSignalType.REFETCH_TABLE_RECORDS, tableId)

      handleCloseDeleteRecord()
      if (handleDeleteRow) {
        handleDeleteRow(tableRow.id)
      }
    },
  })

  const handleDeleteRecord = async () => {
    try {
      const result = await deleteTableRow({
        variables: { tableRowId: tableRow.id },
      })
      const deleteResult = result.data?.deleteTableRowWithResult
      const errorMessage = getDeleteBlockedMessage(deleteResult)
      const blockingDetails = getBlockingReferenceDetails(deleteResult)

      if (errorMessage) {
        if (blockingDetails) {
          const blockingRowPath = getWebPath({
            objectType: BreadcrumbObjectType.TABLE_ROW,
            objectId: blockingDetails.blockingRowId,
          })

          snackbar.showError(
            getDeleteBlockedLinkMessage({
              blockingRowPath,
              otherBlockingCount: blockingDetails.otherBlockingCount,
              rowName: blockingDetails.blockingRowLabel,
              columnName: blockingDetails.columnName,
              tableName: blockingDetails.tableName,
            }),
          )
        } else {
          snackbar.showError(errorMessage)
        }
        return
      }

      snackbar.showSuccess(t('record.delete_success'))
      if (!isChildTableRecord) {
        navigate(getWebPath({ objectType: BreadcrumbObjectType.TABLE, objectId: tableId }), {
          replace: true,
        })
      }
    } catch (error) {
      snackbar.showError(error instanceof Error ? error.message : 'Failed to delete record')
    }
  }

  // Determine description based on pre-flight check
  const getDescription = (): ReactNode => {
    if (isCheckingBlocking) {
      return t('record.checking_dependencies')
    }

    if (preflightResult?.isBlocked && preflightResult.allReferences.length > 0) {
      // Convert to BlockingReferenceDisplay format
      const references: BlockingReferenceDisplay[] = preflightResult.allReferences.map((ref) => ({
        referencingRowId: ref.rowId,
        referencingRowName: ref.rowName,
        blockedRowName: tableRow.name,
        columnName: ref.columnName,
        tableName: ref.tableName,
        tableId: ref.tableId,
      }))

      return (
        <BlockingReferencesDisplay
          headerMessage={t('record.cannot_delete_referenced')}
          maxHeight="max-h-64"
          references={references}
          showBlockedRowName={false}
        />
      )
    }

    if (preflightResult?.isBlocked) {
      return t('record.preflight_blocked_generic')
    }

    return t('record.delete_warning')
  }

  const isBlocked = preflightResult?.isBlocked ?? false
  const isLoading = isDeleting || isCheckingBlocking

  return (
    <ConfirmationDialog
      checkboxText={isBlocked ? undefined : t('record.yes_delete')}
      confirmLabel={isBlocked ? t('common.actions.close') : t('common.actions.delete')}
      description={getDescription()}
      isLoading={isLoading}
      isOpen={openDialog}
      onClose={handleCloseDeleteRecord}
      onConfirm={isBlocked ? handleCloseDeleteRecord : handleDeleteRecord}
      title={t('record.delete_record_name', { recordName: tableRow.name })}
      variant="destructive"
    />
  )
}
